import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { buildRichText } from '../../pubsub/util/buildRichText';
import { isUploadCompleted } from 'conversations-internal-schema/file-upload/operators/isUploadCompleted';
import { getValidationMessage } from 'conversations-visitor-experience-components/visitor-widget/constants/getValidationMessage';
import I18n from 'I18n';
import { List as ImmutableList } from 'immutable';
import Raven from 'raven-js';
import MicrophoneButton from './MicrophoneButton';
import { usePostExternalApiEventOnMessageInputStateChanged } from '../hooks';
import FileUploadsList from '../../attachments/components/FileUploadsList';
import TextInput from './TextInput';
import AttachmentPickerButton from '../../attachments/components/AttachmentPickerButton';
import { isDesktopSafari } from '../../utils/isSafari';
import getAllowedFileTypes from '../../file-uploads/util/getAllowedFileTypes';
import { isMobileDevice } from 'conversations-visitor-experience-components/visitor-widget/util/isMobileDevice';
import { SendButton } from 'conversations-visitor-experience-components/message-editor/SendButton';
import MessageComposer from 'conversations-visitor-experience-components/message-editor/MessageComposer';
import { ScreenCaptureButton } from '../../screen-capture/ScreenCaptureButton';
import { MessageEditorTooltip } from './MessageEditorTooltip';
import { handleInputTextChanged } from '../../post-message/handleInputTextChanged';
import { useAccessibilityContext } from 'conversations-visitor-message-history/accessibility/AccessibilityContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
let HAS_RAVEN_FIRED = false;
const COMPOSITION_ENTER_KEY_CODE = 229;
const MessageEditor = /*#__PURE__*/forwardRef(({
  canUploadAttachments,
  chatInputChanged,
  coloring: {
    accentColor
  } = {},
  disabled,
  fileTypes = getAllowedFileTypes(),
  inputStaging,
  maxAttachmentFileSize,
  mobile = false,
  isIOSMobile,
  onAttachmentButtonClick,
  onRemoveAttachment = () => {},
  onSelectAttachmentFiles = () => {},
  onSetMessageTextExternally = () => {},
  publishMessage,
  shouldDisableInitialInputFocus = false,
  shouldHideAttachmentButton,
  shouldSendInputStagingEvent,
  disclaimerText,
  showQuickReplyPlaceholder,
  showingHsBranding,
  speechPocActive = false,
  speechRecordingStatus,
  stagedAttachments = ImmutableList(),
  stagedMessageEditorText,
  toggleOnAndOffInputFocus = () => {},
  isPublishingBlocked,
  screenCaptureEnabled,
  hasConversationStarted = false,
  isDisabledForConsent = false
}, ref) => {
  const [isComposing, setIsComposing] = useState(false);
  const [messageText, setMessageText] = useState(stagedMessageEditorText || '');
  const {
    setIsMessageEditorFocused
  } = useAccessibilityContext();
  useEffect(() => {
    if (stagedMessageEditorText) {
      setMessageText(prev => prev || stagedMessageEditorText);
    }
  }, [stagedMessageEditorText]);
  useEffect(() => {
    return () => {
      setIsMessageEditorFocused(false);
    };
  }, [setIsMessageEditorFocused]);
  const _ref = useRef(null);
  const editorRef = ref !== null && typeof ref === 'object' ? ref : _ref;
  const focusEditor = () => {
    if (editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  };
  useEffect(() => {
    // this does the same as focusEditor, not using function so that we can have exhaustive deps
    if (!disabled && !(shouldDisableInitialInputFocus || isMobileDevice()) && editorRef !== null && editorRef !== void 0 && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editorRef, disabled, shouldDisableInitialInputFocus]);
  const getMessageText = () => {
    return messageText;
  };
  const validationMessage = () => getValidationMessage(getMessageText());
  const hasAttachments = () => {
    return stagedAttachments && stagedAttachments.size;
  };
  const attachmentsAreValid = () => {
    if (!stagedAttachments) {
      return true;
    }
    return stagedAttachments.every(isUploadCompleted);
  };
  const messageContentsAreValid = () => {
    const _messageText = getMessageText();
    const hasTextContent = typeof _messageText === 'string' && _messageText.trim() !== '';
    return !validationMessage() && attachmentsAreValid() && (hasTextContent || hasAttachments());
  };
  const canPublish = () => {
    return messageContentsAreValid() && !disabled && !isPublishingBlocked;
  };
  const handleChange = useCallback(e => {
    const currentMessageText = e.target.value;
    setMessageText(currentMessageText);
    if (shouldSendInputStagingEvent && !currentMessageText) {
      inputStaging(currentMessageText, true);
    }
    chatInputChanged(currentMessageText);

    // https://issues.hubspotcentral.com/browse/CT-6256
    if ((disabled || isPublishingBlocked) && !HAS_RAVEN_FIRED) {
      Raven.captureMessage('VISITOR_CAN_TYPE_WHILE_PUBLISH_DISABLED');
      HAS_RAVEN_FIRED = true;
    }
  }, [shouldSendInputStagingEvent, inputStaging, chatInputChanged, disabled, isPublishingBlocked]);
  const handlePublish = () => {
    if (canPublish()) {
      const _messageText = getMessageText();
      publishMessage({
        text: _messageText,
        richText: buildRichText(_messageText)
      });
      setMessageText('');
      inputStaging('', true);
      handleInputTextChanged('');
      focusEditor();
    }
  };
  onSetMessageTextExternally(useCallback((text, sendMessage) => {
    if (text) {
      handleChange({
        target: {
          value: text
        }
      });
      if (sendMessage && !disabled && (!hasConversationStarted || !isPublishingBlocked)) {
        publishMessage({
          text,
          richText: buildRichText(text)
        });
        setMessageText('');
        inputStaging('', true);
        handleInputTextChanged('');
      }
    }
  }, [handleChange, publishMessage, inputStaging, disabled, isPublishingBlocked, hasConversationStarted]));
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.shiftKey === false && !isComposing) {
      // Checking deprecated property keyCode for value of 229 to prevent
      // Safari bug that publishes while still composing for Japanese input
      // https://bugs.webkit.org/show_bug.cgi?id=165004
      if (isDesktopSafari() && e.keyCode === COMPOSITION_ENTER_KEY_CODE) {
        return true;
      }
      handlePublish();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };
  const handleSelectAttachmentFiles = ({
    files
  }) => {
    onSelectAttachmentFiles({
      files
    });
    focusEditor();
  };
  const onInputFocus = () => {
    if (!disabled) {
      toggleOnAndOffInputFocus(true);
      setIsMessageEditorFocused(true);
    }
  };
  const onInputBlur = () => {
    toggleOnAndOffInputFocus(false);
    if (!isComposing) {
      setIsMessageEditorFocused(false);
    }
  };
  const handlePaste = e => {
    var _e$clipboardData;
    if (e !== null && e !== void 0 && (_e$clipboardData = e.clipboardData) !== null && _e$clipboardData !== void 0 && _e$clipboardData.files && canUploadAttachments) {
      onSelectAttachmentFiles({
        files: e.clipboardData.files
      });
    }
  };
  const isTextInputDisabled = disabled || isPublishingBlocked;
  const TextInputComponent = /*#__PURE__*/_jsx(TextInput, {
    disableInitialInputFocus: shouldDisableInitialInputFocus,
    disabled: isTextInputDisabled,
    error: Boolean(validationMessage()),
    messageText: messageText,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    showQuickReplyPlaceholder: showQuickReplyPlaceholder,
    ref: editorRef,
    validationMessage: validationMessage(),
    isIOSMobile: isIOSMobile,
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur,
    onPaste: handlePaste,
    onCompositionStart: () => setIsComposing(true),
    onCompositionEnd: () => {
      setIsComposing(false);
      if ((editorRef === null || editorRef === void 0 ? void 0 : editorRef.current) !== document.activeElement) {
        setIsMessageEditorFocused(false);
      }
    },
    hasConversationStarted: hasConversationStarted,
    isDisabledForConsent: isDisabledForConsent
  });
  const FileUploads = hasAttachments() ? /*#__PURE__*/_jsx(FileUploadsList, {
    className: 'm-bottom-2' + (showingHsBranding ? " m-top-2" : ""),
    stagedAttachments: stagedAttachments,
    onRemoveAttachment: attachment => onRemoveAttachment({
      attachment
    })
  }) : null;
  const isDisabled = !canPublish();
  usePostExternalApiEventOnMessageInputStateChanged({
    shouldSendEvent: shouldSendInputStagingEvent,
    disabled: isTextInputDisabled
  });
  return /*#__PURE__*/_jsx(MessageComposer, {
    inputToRender: TextInputComponent,
    FileUploads: FileUploads,
    showingHsBranding: showingHsBranding,
    accentColor: accentColor,
    disclaimerText: disclaimerText,
    disabled: isTextInputDisabled,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MicrophoneButton, {
        disabled: disabled,
        speechRecordingStatus: speechRecordingStatus,
        speechPocActive: speechPocActive,
        mobile: mobile
      }), !shouldHideAttachmentButton ? /*#__PURE__*/_jsxs("div", {
        className: mobile ? 'm-right-3' : "",
        children: [screenCaptureEnabled && /*#__PURE__*/_jsx(ScreenCaptureButton, {
          disabled: !hasConversationStarted || !canUploadAttachments || disabled,
          mobile: mobile,
          hasConversationStarted: hasConversationStarted
        }), /*#__PURE__*/_jsx(AttachmentPickerButton, {
          accept: fileTypes,
          disabled: !hasConversationStarted || !canUploadAttachments || disabled,
          hasConversationStarted: hasConversationStarted,
          mobile: mobile,
          onClick: onAttachmentButtonClick,
          onSelectFiles: handleSelectAttachmentFiles,
          maxAttachmentFileSize: maxAttachmentFileSize
        })]
      }) : null, /*#__PURE__*/_jsx(MessageEditorTooltip, {
        content: isDisabled && !getMessageText().trim() ? I18n.text('conversations-visitor-experience-components.chatTextArea.sendDisabledTooltip') : undefined,
        placement: "top right",
        children: /*#__PURE__*/_jsx(SendButton, {
          disabled: isDisabled,
          onClick: handlePublish
        })
      })]
    })
  });
});
MessageEditor.displayName = 'MessageEditor';
export default MessageEditor;